import React, { memo, useEffect, useId, useRef, useState } from 'react'

import type RLottieInstance from '../../lib/rlottie/RLottie'

import { requestMeasure } from '../../lib/fasterdom/fasterdom'
import { ensureRLottie, getRLottie } from '../../lib/rlottie/RLottie.async'
import buildClassName from '../../util/buildClassName'
import generateUniqueId from '../../util/generateUniqueId'

import useEffectWithPrevDeps from '../../hooks/useEffectWithPrevDeps'
import useFlag from '../../hooks/useFlag'
import usePriorityPlaybackCheck, { isPriorityPlaybackActive } from '../../hooks/usePriorityPlaybackCheck'
import useSharedIntersectionObserver from '../../hooks/useSharedIntersectionObserver'
import { useStateRef } from '../../hooks/useStateRef'
import useThrottledCallback from '../../hooks/useThrottledCallback'
import useLastCallback from '../../hooks/useLastCallback'
import useHeavyAnimationCheck, { isHeavyAnimating } from '../../hooks/useHeavyAnimationCheck'
import useBackgroundMode, { isBackgroundModeActive } from '../../hooks/useBackgroundMode'

export type OwnProps = {
  ref?: React.RefObject<HTMLDivElement | null>
  renderId?: string
  className?: string
  style?: React.CSSProperties
  tgsUrl?: string
  play?: boolean | string
  playSegment?: [number, number]
  seekToEnd?: boolean
  speed?: number
  noLoop?: boolean
  size: number
  quality?: number
  color?: [number, number, number]
  isLowPriority?: boolean
  forceAlways?: boolean
  forceOnHeavyAnimation?: boolean
  sharedCanvasRef?: React.RefObject<HTMLCanvasElement | null>
  sharedCanvasCoords?: { x: number; y: number }
  onClick?: NoneToVoidFunction
  onMouseEnter?: NoneToVoidFunction
  onMouseLeave?: NoneToVoidFunction
  onLoad?: NoneToVoidFunction
  onEnded?: NoneToVoidFunction
  onLoop?: NoneToVoidFunction
  onFrame?: (index: number) => void
}

const THROTTLE_MS = 150

const AnimatedSticker = ({
  ref,
  renderId,
  className,
  style,
  tgsUrl,
  play,
  playSegment,
  speed,
  seekToEnd,
  noLoop,
  size,
  quality,
  isLowPriority,
  color,
  forceAlways,
  forceOnHeavyAnimation,
  sharedCanvasRef,
  sharedCanvasCoords,
  onClick,
  onMouseEnter,
  onMouseLeave,
  onLoad,
  onEnded,
  onLoop,
  onFrame,
}: OwnProps) => {
  let containerRef = useRef<HTMLDivElement>(null)
  if (ref) {
    containerRef = ref
  }

  const viewId = useId()

  const [animation, setAnimation] = useState<RLottieInstance>()
  const animationRef = useRef<RLottieInstance>(undefined)
  const isFirstRender = useRef(true)

  const playKey = play || (play === false ? false : playSegment)
  const playRef = useStateRef(play)
  const playSegmentRef = useStateRef(playSegment)

  const shouldForceOnHeavyAnimation = forceAlways || forceOnHeavyAnimation
  // Delay initialization until heavy animation ends
  const [canInitialize, markCanInitialize, unmarkCanInitialize] = useFlag(
    !isHeavyAnimating() || shouldForceOnHeavyAnimation,
  )
  useHeavyAnimationCheck(unmarkCanInitialize, markCanInitialize, shouldForceOnHeavyAnimation)
  useEffect(() => {
    if (shouldForceOnHeavyAnimation) {
      markCanInitialize()
    }
  }, [shouldForceOnHeavyAnimation])

  const isUnmountedRef = useRef(false)
  useEffect(() => {
    return () => {
      isUnmountedRef.current = true
    }
  }, [])

  const init = useLastCallback(() => {
    if (
      animationRef.current ||
      isUnmountedRef.current ||
      !tgsUrl ||
      // ----------------------------
      (sharedCanvasRef?.current &&
        (!sharedCanvasCoords || !sharedCanvasRef.current?.offsetWidth || !sharedCanvasRef.current?.offsetHeight)) ||
      // ----------------------------
      (isHeavyAnimating() && !shouldForceOnHeavyAnimation)
    ) {
      return
    }

    const container = containerRef.current || sharedCanvasRef?.current
    if (!container) {
      return
    }

    const newAnimation = getRLottie().init(
      tgsUrl,
      container,
      renderId || generateUniqueId(),
      {
        size,
        noLoop,
        quality,
        isLowPriority,
        coords: sharedCanvasCoords,
      },
      viewId,
      color,
      onLoad,
      onEnded,
      onLoop,
      onFrame,
    )

    if (speed) {
      newAnimation.setSpeed(speed)
    }

    if (seekToEnd) {
      newAnimation.seekToEnd()
    }

    setAnimation(newAnimation)
    animationRef.current = newAnimation
  })

  useEffect(() => {
    if (!canInitialize) {
      return
    }
    if (getRLottie()) {
      init()
    } else {
      ensureRLottie().then(init)
    }
  }, [init, tgsUrl, sharedCanvasRef, sharedCanvasCoords, canInitialize])

  const throttledInit = useThrottledCallback(init, [init], THROTTLE_MS)
  useSharedIntersectionObserver(sharedCanvasRef, throttledInit)

  useEffect(() => {
    animation?.setColor(color)
  }, [color, animation])

  useEffect(() => {
    if (typeof speed === 'number') {
      animation?.setSpeed(speed)
    }
  }, [speed, animation])

  useEffect(() => {
    return () => {
      animationRef.current?.removeView(viewId)
    }
  }, [viewId])

  const playAnimation = useLastCallback((shouldRestart = false) => {
    if (!animation || !(playRef.current || playSegmentRef.current) || isFrozen(forceAlways)) {
      return
    }

    if (playSegmentRef.current) {
      animation.playSegment(playSegmentRef.current, shouldRestart, viewId)
    } else if (seekToEnd) {
      animation.seekToEnd()
    } else {
      animation.play(shouldRestart, viewId)
    }
  })

  const playAnimationOnRaf = useLastCallback(() => {
    requestMeasure(playAnimation)
  })

  const pauseAnimation = useLastCallback(() => {
    if (animation?.isPlaying()) {
      animation.pause(viewId)
    }
  })

  useEffectWithPrevDeps(
    ([prevNoLoop]) => {
      if (prevNoLoop !== undefined && noLoop !== prevNoLoop) {
        animation?.setNoLoop(noLoop)
      }
    },
    [noLoop, animation],
  )

  useEffectWithPrevDeps(
    ([prevSharedCanvasCoords]) => {
      if (prevSharedCanvasCoords !== undefined && sharedCanvasCoords !== prevSharedCanvasCoords) {
        animation?.setSharedCanvasCoords(viewId, sharedCanvasCoords)
      }
    },
    [sharedCanvasCoords, viewId, animation],
  )

  useEffect(() => {
    if (!animation) {
      return
    }

    if (playKey) {
      if (!isFrozen(forceAlways, forceOnHeavyAnimation)) {
        playAnimation(noLoop)
      }
    } else {
      pauseAnimation()
    }
  }, [animation, playKey, noLoop, playAnimation, pauseAnimation, forceAlways, forceOnHeavyAnimation])

  useEffect(() => {
    if (animation) {
      if (isFirstRender.current) {
        isFirstRender.current = false
      } else if (tgsUrl) {
        animation.changeData(tgsUrl)
        playAnimation()
      }
    }
  }, [playAnimation, animation, tgsUrl])

  useHeavyAnimationCheck(pauseAnimation, playAnimation, !playKey || shouldForceOnHeavyAnimation)
  usePriorityPlaybackCheck(pauseAnimation, playAnimation, !playKey || forceAlways)
  // Pausing frame may not happen in background,
  // so we need to make sure it happens right after focusing,
  // then we can play again.
  useBackgroundMode(pauseAnimation, playAnimationOnRaf, !playKey || forceAlways)

  if (sharedCanvasRef) {
    return undefined
  }

  return (
    <div
      ref={containerRef}
      className={buildClassName('AnimatedSticker', className)}
      style={{
        width: size !== undefined ? `${size}px` : undefined,
        height: size !== undefined ? `${size}px` : undefined,
        cursor: onClick ? 'pointer' : undefined,
        ...style,
      }}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    />
  )
}

export default memo(AnimatedSticker)

function isFrozen(forceAlways = false, forceOnHeavyAnimation = false) {
  if (forceAlways) {
    return false
  }
  return (!forceOnHeavyAnimation && isHeavyAnimating()) || isPriorityPlaybackActive() || isBackgroundModeActive()
}
